/**
 * **RPC：信封的分派 + 校验阶梯 + 在飞的请求表。**
 *
 * 这里是 `old_repo` 的 `#handleIncomingMessage` 那一套的**重写版**：
 * 保留它的形状（一张方法表 + 逐级收窄的校验 + 结构化报障），
 * 换掉它的地基（p2pcf 的裸字节流 → `Transport` 接口）。
 *
 * 它**不认识业务**：`handle()` 注册的每个方法自带一个 zod schema，
 * 载荷校验用的就是那个 schema。所以这一层可以被任何协议复用。
 *
 * ## 校验阶梯（`docs/netcode-s3.md` §5.1）
 *
 * ```
 * 不是 string        → BadData
 * JSON.parse 失败    → BadJSON
 * 信封不合 schema    → BadMessage / BadRequest / BadResponse
 * t 不认识           → BadMessageType
 * method 没注册      → BadMethod
 * 载荷不合业务 schema → BadPayload
 * handler 抛 AccessDenied → AccessDenied
 * handler 抛别的     → HandlerError
 * 响应对不上在飞的请求 → OrphanResponse
 * ```
 *
 * 每一级失败都回一张报障单，**绝不静默丢弃**。
 * 唯一的例外是"报障单自己也解析不出来" —— 那种情况只记日志，
 * 因为两个版本不一致的实现互相报障会没完没了。
 *
 * ## 业务否决**不是**协议故障
 *
 * `reduce` 返回 `{ok:false, reason}` 时，房主回的是一次**正常的响应**，
 * 响应体就是那个否决。只有"你这包东西根本不是协议"才走 `pardon`。
 * 这条区分让调用方只需要处理一次异常，而"现在不是你的回合"这种
 * 日常情形走的是返回值。
 *
 * ## 不做应用层重传
 *
 * DataChannel 默认就是可靠有序的（`pc.createDataChannel("data")`，无参数），
 * 所以 `old_repo` 那套 seq/ack/`maxRetries`/`rto·2^n` 是在 SCTP 之上又做了一遍
 * 可靠性。这里只保留**超时判定**，而且它由传输层执行
 * （`docs/archive/netcode-s3-evaluation.md` §4）。
 */

import { z } from 'zod';
import { t } from '../i18n';
import {
  AccessDeniedError,
  S3Error,
  decodeEnvelope,
  describeError,
  encodeMessage,
  encodePardon,
  encodeRequest,
  encodeResponse,
  nextMessageId,
  type PardonEnvelope,
  type PardonInput,
} from './protocol';
import type { Transport, Unsubscribe } from './transport';

export interface RpcContext {
  /** 这条消息是谁发的。**只有 peer id** —— 上层拿不到 peer 对象 */
  readonly from: string;
}

export type RpcHandler = (payload: unknown, ctx: RpcContext) => unknown | Promise<unknown>;
export type NotifyHandler = (payload: unknown, from: string) => void;

export interface PardonReport {
  readonly reason: string;
  readonly details: unknown;
  readonly rawText: string | null;
}

export interface RpcOptions {
  /** 默认请求超时。**只有传输层知道多久没动静算死**，所以它只是个默认值 */
  readonly defaultTimeoutMs?: number;
  /** 收到远端报障单时回调（观测用） */
  readonly onPardon?: (from: string, pardon: PardonReport) => void;
  /** 本地异常（分派崩了、传输报错）时回调。默认 `console.error` */
  readonly onError?: (error: unknown) => void;
}

export interface Rpc {
  /**
   * 注册一个请求方法。
   *
   * ⚠️ 同名方法**只能注册一次**，重复注册直接抛 —— 方法名里带着 session id
   * （`s3/session/<id>/view`），撞名说明有人在拼错名字或者忘了注销，
   * 这种 bug 静默覆盖是很难查的。
   */
  handle<T>(method: string, schema: z.ZodType<T>, handler: (payload: T, ctx: RpcContext) => unknown | Promise<unknown>): Unsubscribe;

  /** 发通知。`to` 省略或 `null` = 广播 */
  notify(method: string, payload: unknown, to?: string | null): void;

  /** 监听通知。同一个方法可以有多个监听者 */
  onNotify<T>(method: string, schema: z.ZodType<T>, handler: (payload: T, from: string) => void): Unsubscribe;

  /** 请求 + 校验响应体。协议/传输故障抛 `S3Error`；业务否决在返回值里 */
  request<T>(to: string, method: string, payload: unknown, schema: z.ZodType<T>, timeoutMs?: number): Promise<T>;

  /** 在飞的请求数。诊断与测试用 */
  pendingCount(): number;

  /** 远端报障单（观测用） */
  onPardon(handler: (from: string, pardon: PardonReport) => void): Unsubscribe;

  /** 本地异常（观测用） */
  onError(handler: (error: unknown) => void): Unsubscribe;

  dispose(): void;
}

const DEFAULT_TIMEOUT_MS = 8000;

interface MethodEntry {
  readonly schema: z.ZodType;
  readonly handler: RpcHandler;
}

interface InFlight {
  readonly method: string;
  readonly abort: (error: unknown) => void;
}

export function createRpc(transport: Transport, options: RpcOptions = {}): Rpc {
  const defaultTimeoutMs = options.defaultTimeoutMs ?? DEFAULT_TIMEOUT_MS;

  const methods = new Map<string, MethodEntry>();
  const notifyListeners = new Map<string, Set<NotifyHandler>>();
  const notifySchemas = new Map<string, z.ZodType>();
  const inFlight = new Map<string, InFlight>();
  const pardonListeners = new Set<(from: string, pardon: PardonReport) => void>();
  const errorListeners = new Set<(error: unknown) => void>();

  let disposed = false;

  const reportError = (error: unknown): void => {
    if (options.onError !== undefined) options.onError(error);
    else console.error(`[s3] ${t('host.rpc.localError')}`, error);
    for (const listener of errorListeners) listener(error);
  };

  const emitPardon = (from: string, pardon: PardonReport): void => {
    if (options.onPardon !== undefined) options.onPardon(from, pardon);
    for (const listener of pardonListeners) listener(from, pardon);
  };

  const sendPardon = (to: string, input: PardonInput): void => {
    transport.send(to, encodePardon(input));
  };

  // ============================================================
  // 通知通道
  // ============================================================

  const handleEnvelopeOnMessage = (from: string, raw: string): void => {
    const decoded = decodeEnvelope(raw);
    if (!decoded.ok) {
      reportError(new S3Error(decoded.pardon.reason, t('host.rpc.undecodable', { from }), decoded.pardon));
      return;
    }
    const envelope = decoded.envelope;
    switch (envelope.t) {
      case 'pardon': {
        // 对端的报障单。永远不回一张报障单给它
        emitPardon(from, { reason: envelope.reason, details: envelope.details, rawText: envelope.rawText });
        return;
      }
      case 'msg': {
        dispatchNotify(from, envelope.method, envelope.payload, raw);
        return;
      }
      case 'res':
      case 'req': {
        // 走错了通道：请求/响应必须走请求通道
        sendPardon(from, {
          id: envelope.t === 'res' ? envelope.id : null,
          reason: envelope.t === 'res' ? 'BadResponse' : 'BadRequest',
          details: t('host.rpc.envelopeOnNotify', { t: envelope.t }),
          rawText: raw,
        });
        return;
      }
    }
  };

  const dispatchNotify = (from: string, method: string, payload: unknown, raw: string): void => {
    const listeners = notifyListeners.get(method);
    if (listeners === undefined || listeners.size === 0) {
      sendPardon(from, { reason: 'BadMethod', details: method, rawText: raw });
      return;
    }
    const schema = notifySchemas.get(method);
    let value: unknown = payload;
    if (schema !== undefined) {
      const parsed = schema.safeParse(payload);
      if (!parsed.success) {
        sendPardon(from, { reason: 'BadPayload', details: z.treeifyError(parsed.error), rawText: raw });
        return;
      }
      value = parsed.data;
    }
    for (const listener of listeners) {
      try {
        listener(value, from);
      } catch (error) {
        reportError(error);
        sendPardon(from, { reason: 'HandlerError', details: describeError(error), rawText: raw });
      }
    }
  };

  // ============================================================
  // 请求通道
  // ============================================================

  const handleEnvelopeOnRequest = async (from: string, raw: string): Promise<string> => {
    const decoded = decodeEnvelope(raw);
    if (!decoded.ok) return encodePardon(decoded.pardon);
    const envelope = decoded.envelope;

    if (envelope.t !== 'req') {
      // `pardon` 也可能出现在这儿：对端把"报障"当成了请求来发。
      // 无论哪种，都回一条 BadRequest 让它知道通道用错了
      return encodePardon({
        id: envelope.t === 'res' || envelope.t === 'pardon' ? envelope.id : null,
        reason: 'BadRequest',
        details: t('host.rpc.envelopeOnRequest', { t: envelope.t }),
        rawText: raw,
      });
    }

    const entry = methods.get(envelope.method);
    if (entry === undefined) {
      return encodePardon({ id: envelope.id, reason: 'BadMethod', details: envelope.method, rawText: raw });
    }

    const parsed = entry.schema.safeParse(envelope.payload);
    if (!parsed.success) {
      return encodePardon({ id: envelope.id, reason: 'BadPayload', details: z.treeifyError(parsed.error), rawText: raw });
    }

    try {
      return encodeResponse(envelope.id, await entry.handler(parsed.data, { from }));
    } catch (error) {
      if (error instanceof AccessDeniedError) {
        return encodePardon({ id: envelope.id, reason: 'AccessDenied', details: error.message, rawText: raw });
      }
      reportError(error);
      return encodePardon({ id: envelope.id, reason: 'HandlerError', details: describeError(error), rawText: raw });
    }
  };

  const offMessage = transport.onMessage((from, raw) => {
    try {
      handleEnvelopeOnMessage(from, raw);
    } catch (error) {
      reportError(error);
    }
  });

  const offRequest = transport.onRequest((from, raw) => {
    try {
      return handleEnvelopeOnRequest(from, raw);
    } catch (error) {
      // `handleEnvelopeOnRequest` 自己已经兜过底了；走到这里说明是更外层
      // （比如 `encodePardon` 本身）出了意外。仍然要给对端一条响应，
      // 否则它只能干等到超时
      reportError(error);
      return encodePardon({ reason: 'InternalError', details: describeError(error), rawText: raw, parseFailed: true });
    }
  });

  // ============================================================
  // 公开接口
  // ============================================================

  const rpc: Rpc = {
    handle<T>(method: string, schema: z.ZodType<T>, handler: (payload: T, ctx: RpcContext) => unknown) {
      if (methods.has(method)) {
        throw new Error(`[s3] ${t('host.rpc.duplicateMethod', { method })}`);
      }
      // 校验器只保证"跑完 schema 之后载荷确实是 T"；这里的表按 `unknown` 存，
      // 类型由 `handle<T>` 的签名在**注册点**保证
      methods.set(method, { schema, handler: handler as (payload: unknown, ctx: RpcContext) => unknown });
      return () => {
        methods.delete(method);
      };
    },

    notify(method, payload, to = null) {
      if (disposed) return;
      transport.send(to, encodeMessage(method, payload));
    },

    onNotify<T>(method: string, schema: z.ZodType<T>, handler: (payload: T, from: string) => void) {
      let listeners = notifyListeners.get(method);
      if (listeners === undefined) {
        listeners = new Set();
        notifyListeners.set(method, listeners);
      }
      listeners.add(handler as (payload: unknown, from: string) => void);
      notifySchemas.set(method, schema);
      return () => {
        const current = notifyListeners.get(method);
        current?.delete(handler as (payload: unknown, from: string) => void);
        if (current !== undefined && current.size === 0) {
          notifyListeners.delete(method);
          notifySchemas.delete(method);
        }
      };
    },

    async request<T>(to: string, method: string, payload: unknown, schema: z.ZodType<T>, timeoutMs?: number): Promise<T> {
      if (disposed) throw new S3Error('Disconnected', t('host.rpc.disposed'));
      const id = nextMessageId();

      // 记在在飞的表里，这样 `dispose()` 能把它们一次性打掉，
      // 而不是让调用方永远挂在一个不会落地的 Promise 上
      const inFlightPromise = new Promise<never>((_resolve, reject) => {
        inFlight.set(id, { method, abort: reject });
      });
      // 避免"没人接的 rejection"警告 —— 下面用 race 接住它
      inFlightPromise.catch(() => {});

      const responseRaw = await Promise.race([
        transport.request(to, encodeRequest(id, method, payload), timeoutMs ?? defaultTimeoutMs),
        inFlightPromise,
      ]).finally(() => {
        inFlight.delete(id);
      }).catch((error: unknown) => {
        throw toS3Error(error, method);
      });

      const decoded = decodeEnvelope(responseRaw);
      if (!decoded.ok) {
        throw new S3Error('BadResponse', t('host.rpc.responseUndecodable', { to, method }), decoded.pardon);
      }
      const envelope = decoded.envelope;
      if (envelope.t === 'pardon') {
        throw fromPardon(envelope, method);
      }
      if (envelope.t !== 'res') {
        throw new S3Error('BadResponse', t('host.rpc.responseNotResponse', { to, method }), envelope);
      }
      if (envelope.id !== id) {
        throw new S3Error('OrphanResponse', t('host.rpc.responseIdMismatch', { expected: id, received: envelope.id }), envelope);
      }

      const parsed = schema.safeParse(envelope.payload);
      if (!parsed.success) {
        throw new S3Error('BadResponse', t('host.rpc.responseBadPayload', { to, method }), z.treeifyError(parsed.error));
      }
      return parsed.data;
    },

    pendingCount() {
      return inFlight.size;
    },

    onPardon(handler) {
      pardonListeners.add(handler);
      return () => pardonListeners.delete(handler);
    },

    onError(handler) {
      errorListeners.add(handler);
      return () => errorListeners.delete(handler);
    },

    dispose() {
      if (disposed) return;
      disposed = true;
      offMessage();
      offRequest();
      for (const [, entry] of inFlight) {
        entry.abort(new S3Error('Disconnected', t('host.rpc.disposedPending', { method: entry.method })));
      }
      inFlight.clear();
      methods.clear();
      notifyListeners.clear();
      notifySchemas.clear();
      pardonListeners.clear();
      errorListeners.clear();
    },
  };

  return rpc;
}

function fromPardon(envelope: PardonEnvelope, method: string): S3Error {
  return new S3Error(envelope.reason, t('host.rpc.peerPardon', { method, reason: envelope.reason }), {
    details: envelope.details,
    rawText: envelope.rawText,
  });
}

/**
 * 把传输层的异常翻译成统一的 `S3Error`。
 *
 * 这一层翻译**必须存在**：上层不该知道 Trystero 的 `error.kind` 这个约定
 * （那正是 `old_repo` 把 `TinySimplePeer` 漏出去之后付出的代价）。
 */
export function toS3Error(error: unknown, method: string): S3Error {
  if (error instanceof S3Error) return error;
  const kind = (error as { kind?: string } | null)?.kind;
  switch (kind) {
    case 'timeout':
      return new S3Error('Timeout', t('host.rpc.timeout', { method }), error);
    case 'aborted':
      return new S3Error('Aborted', t('host.rpc.aborted', { method }), error);
    case 'rejected':
      return new S3Error('RemoteRejected', t('host.rpc.rejected', { method }), error);
    default:
      return new S3Error('Disconnected', t('host.rpc.undelivered', { method }), describeError(error));
  }
}
